import {
  OperatorPerformance,
  OperatorPerformanceBucket,
  UnifiedMonitoringOperatorPerformanceMessage,
} from '@hakimo-ui/hakimo/types';

export const MINUTE_MS = 60_000;
// Pulse's publish cycle; event-flow's STALE_AFTER_MS is two cycles.
export const REFRESH_MINUTES = 15;
export const DELAYED_AFTER_MINUTES = 30;
// Pulse's panel window; only used when there is no panel to read it from.
export const DEFAULT_WINDOW_HOURS = 6;

export type OperatorPerformanceView =
  | { kind: 'hidden' }
  | { kind: 'empty' }
  | { kind: 'ready'; data: OperatorPerformance; stale: boolean };

const isTimestamp = (value: unknown) =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value));

function isPanel(payload: unknown): payload is OperatorPerformance {
  const panel = payload as Partial<OperatorPerformance>;
  return (
    typeof panel.totals === 'object' &&
    panel.totals !== null &&
    Array.isArray(panel.series) &&
    Number.isFinite(panel.totals.medianResolutionRate) &&
    Number(panel.resolutionMinutes) > 0 &&
    isTimestamp(panel.generatedAt) &&
    isTimestamp(panel.windowStart) &&
    isTimestamp(panel.windowEnd)
  );
}

/**
 * No message yet, no panel while Pulse is not publishing, or a malformed panel
 * hides the chip, so the page is unchanged until the backend is live.
 */
export function toView(
  message: UnifiedMonitoringOperatorPerformanceMessage | null
): OperatorPerformanceView {
  if (!message) {
    return { kind: 'hidden' };
  }
  if (!message.payload) {
    return message.stale ? { kind: 'hidden' } : { kind: 'empty' };
  }
  if (!isPanel(message.payload)) {
    return { kind: 'hidden' };
  }
  return { kind: 'ready', data: message.payload, stale: message.stale };
}

/** Every bucket in the window; Pulse sends active buckets only. */
export function fillBuckets(
  data: OperatorPerformance
): OperatorPerformanceBucket[] {
  const step = data.resolutionMinutes * MINUTE_MS;
  const start = Date.parse(data.windowStart);
  const end = Date.parse(data.windowEnd);
  if (!(step > 0) || Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return [];
  }
  const byStart = new Map(
    data.series.map((bucket) => [Date.parse(bucket.windowStart), bucket])
  );
  const buckets: OperatorPerformanceBucket[] = [];
  for (let t = start; t < end; t += step) {
    buckets.push(
      byStart.get(t) ?? {
        windowStart: new Date(t).toISOString(),
        handled: 0,
        resolved: 0,
        escalated: 0,
      }
    );
  }
  return buckets;
}

export function getWindowHours(data: OperatorPerformance): number {
  const hours =
    (Date.parse(data.windowEnd) - Date.parse(data.windowStart)) /
    (60 * MINUTE_MS);
  return Number.isFinite(hours) && hours > 0
    ? Math.round(hours)
    : DEFAULT_WINDOW_HOURS;
}

export interface Freshness {
  delayed: boolean;
  label: string;
  shortLabel: string;
}

function formatAge(minutes: number, unit: 'min' | 'm'): string {
  if (minutes < 1) {
    return 'just now';
  }
  if (minutes < 60) {
    return `${minutes}${unit === 'min' ? ' min' : 'm'} ago`;
  }
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
}

/**
 * Age is measured from the snapshot time in the browser, so it keeps counting
 * up when the same snapshot is sent again.
 */
export function getFreshness(
  generatedAt: string,
  stale: boolean,
  now: number
): Freshness {
  const at = Date.parse(generatedAt);
  const minutes = Number.isNaN(at)
    ? 0
    : Math.max(0, Math.floor((now - at) / MINUTE_MS));
  const delayed = stale || minutes >= DELAYED_AFTER_MINUTES;
  return {
    delayed,
    label: `${delayed ? 'Delayed · updated' : 'Updated'} ${formatAge(
      minutes,
      'min'
    )}`,
    shortLabel: `${delayed ? 'Delayed · ' : ''}${formatAge(minutes, 'm')}`,
  };
}

/** 8 → "8"; 7.5 → "7.5". */
export function formatRate(rate: number): string {
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(1);
}
